import type { OnboardingRequirement, OnboardingRun, OnboardingStage } from '~/types/models';

function toMillis(value: unknown): number {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds: number }).seconds || 0) * 1000;
    }

    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function sortOnboardingStages(stages: OnboardingStage[]): OnboardingStage[] {
    return [...stages].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

export function countCompletedRequirements(requirements: OnboardingRequirement[]): number {
    return requirements.filter((requirement) => ['completed', 'skipped'].includes(requirement.status)).length;
}

export function calculateOnboardingProgress(requirements: OnboardingRequirement[]): number {
    if (!requirements.length) return 0;
    return Math.round((countCompletedRequirements(requirements) / requirements.length) * 100);
}

export function getOnboardingRequirementCounts(requirements: OnboardingRequirement[]) {
    return requirements.reduce((counts, requirement) => {
        counts.total += 1;
        counts[requirement.status] = (counts[requirement.status] || 0) + 1;
        return counts;
    }, {
        total: 0,
        pending: 0,
        in_progress: 0,
        completed: 0,
        blocked: 0,
        skipped: 0,
    } as Record<OnboardingRequirement['status'] | 'total', number>);
}

export function getOnboardingFrictionFlags(run: OnboardingRun, now = Date.now()): string[] {
    const flags = new Set<string>();
    const requirements = Array.isArray(run.requirements) ? run.requirements : [];
    const staleAfterMs = 5 * 24 * 60 * 60 * 1000;
    const updatedAtMs = toMillis(run.updatedAt || run.createdAt || run.startedAt);

    if (requirements.some((requirement) => requirement.status === 'blocked')) {
        flags.add('Blocked requirement');
    }

    if (requirements.some((requirement) => requirement.type === 'portal_activation' && requirement.status !== 'completed')) {
        flags.add('Portal not activated');
    }

    if (requirements.some((requirement) => requirement.type === 'form' && !['completed', 'skipped'].includes(requirement.status))) {
        flags.add('Questionnaire incomplete');
    }

    if (requirements.some((requirement) => requirement.type === 'contract' && !['completed', 'skipped'].includes(requirement.status))) {
        flags.add('Contract unsigned');
    }

    if (requirements.some((requirement) => ['invoice', 'payment'].includes(requirement.type) && !['completed', 'skipped'].includes(requirement.status))) {
        flags.add('Payment pending');
    }

    if (updatedAtMs > 0 && now - updatedAtMs > staleAfterMs && run.status === 'active') {
        flags.add('No movement for 5 days');
    }

    return Array.from(flags);
}

export function calculateOnboardingFrictionScore(run: OnboardingRun): number {
    const flags = getOnboardingFrictionFlags(run);
    const blockedCount = run.requirements.filter((requirement) => requirement.status === 'blocked').length;
    return Math.min(100, (flags.length * 18) + (blockedCount * 12));
}

export function getNextOnboardingStage(run: OnboardingRun): OnboardingStage | null {
    const stages = sortOnboardingStages(run.stages || []);
    const currentIndex = stages.findIndex((stage) => stage.id === run.currentStageId);
    if (currentIndex < 0) return stages[0] || null;
    return stages[currentIndex + 1] || null;
}
